import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner } from 'react-bootstrap';

const money = value => new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL',
}).format(Number(value || 0));

const ConnectionAddonsCard = ({ addons, loading, changing, onChange }) => {
  const [drafts, setDrafts] = useState({});
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    setDrafts(Object.fromEntries(addons.map(addon => [addon.key, addon.extra_quantity])));
  }, [addons]);

  const draftFor = addon => drafts[addon.key] ?? addon.extra_quantity;
  const setDraft = (addon, quantity) => setDrafts(current => ({
    ...current, [addon.key]: Math.max(0, quantity),
  }));
  const cancelConfirmation = () => {
    setDrafts(Object.fromEntries(addons.map(addon => [addon.key, addon.extra_quantity])));
    setConfirmation(null);
  };
  const confirmChange = async () => {
    const pending = confirmation;
    setConfirmation(null);
    if (pending) {
      for (const change of pending) await onChange(change.addon, change.quantity);
    }
  };

  const pendingChanges = addons
    .filter(addon => draftFor(addon) !== addon.extra_quantity)
    .map(addon => ({ addon, quantity: draftFor(addon) }));

  return (
  <section style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '16px', background: '#fff' }}>
    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
      <div>
        <h3 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>Conexões adicionais</h3>
        <small className="text-muted">Cada provedor inclui uma conexão. Os adicionais aumentam o limite contratado.</small>
      </div>
      <Badge bg="light" text="dark">Cobrança mensal</Badge>
    </div>
    <Alert variant="info">
      Remover uma conexão em <strong>Configurações &gt; Conexões</strong> não cancela a cobrança.
      Para reduzir o contratado, diminua a quantidade aqui. Se todas as vagas estiverem em uso,
      remova primeiro as conexões excedentes.
    </Alert>
    {loading ? <div className="text-center py-3"><Spinner animation="border" size="sm" /></div> : (
      <div style={{ display: 'grid', gap: '12px' }}>
        {addons.map(addon => (
          <div key={addon.key} className="d-flex flex-wrap justify-content-between align-items-center gap-3" style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '14px' }}>
            <div>
              <div style={{ fontWeight: 800, color: '#0f172a' }}>{addon.name}</div>
              <small className="text-muted">
                {addon.used_quantity} em uso de {addon.total_limit} disponíveis · {money(addon.unit_price / 100)} por adicional/mês
              </small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button size="sm" variant="outline-secondary"
                disabled={!addon.is_available || draftFor(addon) === 0 || changing === addon.key}
                onClick={() => setDraft(addon, draftFor(addon) - 1)}>−</Button>
              <div className="text-center" style={{ minWidth: '88px' }}>
                <strong>{draftFor(addon)}</strong>
                <div className="text-muted" style={{ fontSize: '10px' }}>adicional(is)</div>
              </div>
              <Button size="sm" variant="primary" disabled={!addon.is_available || changing === addon.key}
                onClick={() => setDraft(addon, draftFor(addon) + 1)}>
                {changing === addon.key ? <Spinner animation="border" size="sm" /> : '+'}
              </Button>
            </div>
            {!addon.is_available && <div className="w-100 text-muted small">Adicional aguardando definição de preço pelo administrador.</div>}
          </div>
        ))}
        {pendingChanges.length > 0 && (
          <div className={'d-flex justify-content-end mt-2'}>
            <Button variant={'success'} disabled={!!changing} onClick={() => setConfirmation(pendingChanges)}>
              Revisar e confirmar alterações
            </Button>
          </div>
        )}
      </div>
    )}
    <Modal show={!!confirmation} onHide={cancelConfirmation} centered>
      <Modal.Header closeButton>
        <Modal.Title>Confirmar alteração da assinatura</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {confirmation && (
          <>
            {confirmation.map(change => (
              <div key={change.addon.key} className={'mb-3'}>
                <strong>{change.addon.name}</strong>
                <div className={'text-muted small'}>
                  Quantidade atual: {change.addon.extra_quantity} · Nova quantidade: {change.quantity}<br />
                  Novo valor mensal: {money((change.quantity * change.addon.unit_price) / 100)}
                </div>
              </div>
            ))}
            <Alert variant={'warning'} className={'mb-0'}>
              Ao confirmar, a alteração será enviada à Pagar.me e refletida na cobrança mensal do cartão.
              Quantidade zero cancela o respectivo adicional. Cancelar esta janela descarta o pedido sem enviar cobrança.
            </Alert>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant={'outline-secondary'} onClick={cancelConfirmation}>Cancelar</Button>
        <Button variant={'success'} onClick={confirmChange} disabled={!confirmation || !!changing}>
          Confirmar alteração mensal
        </Button>
      </Modal.Footer>
    </Modal>
  </section>
  );
};

export default ConnectionAddonsCard;
